import { http } from '@pendo/http';
import { generateOldEntityKey } from '@/session-replay/helpers/common.util';
import get from 'lodash/get';
import { RELEVANCE } from '@/session-replay/components/session-replay-insights/helpers/constants';
import { CONCURRENCY_LIMIT, createBatchesToDownload } from '@/session-replay/replay-worker.util';
/**
 * SessionRecordingEntities are deleted in retention based deletion based on firstStartTime. Because of this
 * it is not a simple migration to remove support on the BE. To account for this anytime we make requests to
 * an entity endpoint we'll just add a firstStartTime = startTime which should be sufficient for deletion.
 */
function addFirstAndLastStartTimeToReplayBody (replay) {
    replay.firstStartTime = replay.startTime;
    replay.lastStartTime = replay.endTime;
}

export function createSessionReplayEntity (replayBody) {
    addFirstAndLastStartTimeToReplayBody(replayBody);

    return http.post('/api/session-replay/s/_SID_/session', JSON.stringify(replayBody)).then((res) => {
        return res.data;
    });
}

export async function updateReplayEntity (replayBody) {
    addFirstAndLastStartTimeToReplayBody(replayBody);

    return http.put('/api/session-replay/s/_SID_/session', replayBody).then((res) => res.data);
}

export async function starReplay (replayEntityId) {
    return http.put(`/api/s/_SID_/star/sessionrecording/${replayEntityId}`, {});
}

export function unstarReplay (replayId) {
    return http.delete(`/api/s/_SID_/star/sessionrecording/${replayId}`);
}

export function getStarReplay (replayId) {
    return http.get(`/api/session-replay/s/_SID_/session/${replayId}`).then((res) => res.data);
}

export function getSessionReplayEntities () {
    const initialMap = {};

    return http.get('/api/session-replay/s/_SID_/session').then((res) =>
        res?.data.reduce((map, replay) => {
            const oldRecordingIdKey = generateOldEntityKey(replay);
            map[oldRecordingIdKey] = replay;

            // This is needed to find the replay entities from the library page since they don't
            // have recordingIds, but have a recordingSessionId. Entries need to be temp. doubled
            // to handle both cases. One map uses recordingSessionId as the key, the other uses
            // visitorId_recordingId_accountId as the key.
            if (replay.recordingSessionId) {
                map[replay.recordingSessionId] = replay;
            }

            return map;
        }, initialMap),
    );
}

export function saveClip ({ replayContext, clipStartTime, clipEndTime, description = '' }) {
    const {
        startingPageId,
        appId,
        accountId,
        recordingIds,
        recordingSessionId,
        visitorId,
        minBrowserTime: sessionStartTime,
        maxBrowserTime: sessionEndTime,
        selectedEvents,
        frustration,
        insight,
        inactivityPeriods = [],
        chunks = [],
        startTime,
    } = replayContext;

    if (startingPageId && get(selectedEvents, 'page', []).length) {
        const startingPageIndex = selectedEvents.page.findIndex((id) => id === startingPageId);

        if (startingPageIndex !== -1) {
            // page id is already in the selected events
            selectedEvents.page[startingPageIndex] += '--starting-page';
        } else {
            // add the starting page to the selected events, since it's coming from the product area
            selectedEvents.page.push(`${startingPageId}--starting-page`);
        }
    }

    const inactivityPeriodsBody = inactivityPeriods.map(({ inactivityStart, inactivityEnd }) => ({
        start: inactivityStart + startTime,
        end: inactivityEnd + startTime,
    }));

    const recordingData = chunks.reduce((acc, { recordingId, chunkStartTime, chunkEndTime, chunkSize }) => {
        acc[recordingId] = {
            startTime: chunkStartTime,
            endTime: chunkEndTime,
            size: chunkSize,
        };

        return acc;
    }, {});

    const includeInsight = !!insight && insight.insightType !== RELEVANCE;

    const clipBody = {
        appId,
        accountId,
        recordingIds,
        recordingSessionId,
        visitorId,
        sessionStartTime,
        sessionEndTime,
        clipStartTime,
        clipEndTime,
        description,
        ...(!!selectedEvents && { selectedEvents }),
        ...(!!frustration && { frustration }),
        ...(includeInsight && { insight }),
        ...(!!inactivityPeriodsBody && { inactivityPeriods: inactivityPeriodsBody }),
        ...(!!recordingData && { recordingData }),
    };

    return http.post('/api/session-replay/s/_SID_/clip', clipBody).then((res) => {
        return res.data;
    });
}

export function getSavedClips (createdByMe = true) {
    const params = { createdByMe };

    return http.get('/api/session-replay/s/_SID_/clip', { params }).then((res) => res.data);
}

export async function getSavedRecording ({ id, recordingIds, abortSignal }) {
    const { data } = await http.post(
        `/api/session-replay/s/_SID_/clip/${id}`,
        { recordingIds },
        { signal: abortSignal },
    );

    return data;
}

export async function fetchSavedClipBatches ({ id, recordingData, abortSignal }) {
    const missingChunks = Object.entries(recordingData)
        .sort(([, recordingA], [, recordingB]) => recordingA.startTime - recordingB.startTime)
        .map(([recordingId, { size }]) => ({
            recordingId,
            chunkSize: size,
        }));

    const batches = createBatchesToDownload({ missingChunks });
    const results = new Array(batches.length);
    let nextIndex = 0;

    async function fetchNext () {
        const index = nextIndex++;
        if (index >= batches.length) return;
        results[index] = await getSavedRecording({ id, recordingIds: batches[index], abortSignal });
        await fetchNext();
    }

    const concurrentFetches = Math.min(CONCURRENCY_LIMIT, batches.length);
    await Promise.all(Array.from({ length: concurrentFetches }, fetchNext));

    return results.flat();
}

export async function getSavedClipContext (clipId, abortSignal) {
    const { data } = await http.get(`/api/session-replay/s/_SID_/clip/${clipId}`, { signal: abortSignal });

    return data;
}

export function watchClip (clipId) {
    return http.post(`/api/session-replay/s/_SID_/clip/${clipId}/watch`).then((res) => res.data);
}

export function deleteClip (clipId) {
    return http.delete(`/api/session-replay/s/_SID_/clip/${clipId}`);
}

export function updateSavedClipDescription ({ description, clipId }) {
    const payload = { description };

    return http.put(`/api/session-replay/s/_SID_/clip/${clipId}`, payload).then((res) => res.data);
}
